import type React from "react"
import { useState, useRef, useEffect } from "react"
import { LogOut } from "lucide-react"
import { clearTokens } from "../features/auth/tokenStorage"
import { logout } from "../features/auth/authSlice"
import { useLocation, useSearchParams } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import { fetchCompanyMember, fetchCompany } from "../features/auth/authAPI"
import { updateProfilePicture, updateCompanyProfilePicture, updateCompanyProfilePictureUrl, ensureCurrentUser } from "../features/auth/authSlice"
import { getProfilePictureUrl } from "../utils/imageUtils"
import { ProfileImage } from "./ProfileImage"

type HeaderProps = {
  title?: string
  subtitle?: string
  userImage?: string
}

export const Header: React.FC<HeaderProps> = ({
  title,
  subtitle,
  userImage = "/images/image.png",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dispatch = useAppDispatch();
  // const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const avatarRef = useRef<HTMLButtonElement>(null);
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const user = useAppSelector((state) => state.auth.user);
  const profilePictureId = useAppSelector((state) => state.auth.profilePictureId);
  const companyProfilePictureId = useAppSelector((state) => state.auth.companyProfilePictureId);
  // const userGroups = useAppSelector((state) => state.auth.userGroups);
  // const isCompanyRecruiter = (userGroups ?? []).includes("company_recruiter");
  // const companyProfilePictureUrl = useAppSelector((state) => state.auth.companyProfilePictureUrl);

  // Get title based on current route
  const getTitleFromRoute = (pathname: string): string => {
    if (pathname === "/profile-setting") {
      return "Profile setting";
    }
    if (pathname === "/all-jobs") {
      return "Jobs";
    }
    if (pathname === "/create-job") {
      return "Jobs";
    }
    if (pathname.startsWith("/jobs/")) {
      return "Jobs";
    }
    if (pathname === "/job-matches") {
      return "Jobs";
    }
    if (pathname.startsWith("/candidate/")) {
      return "Jobs";
    }
    if (pathname === "/dashboard") {
      return "Dashboard";
    }
    if (pathname === "/all-members") {
      return "Assign roles";
    }
    if (pathname === "/company-list") {
      return "Registered companies";
    }
    if (pathname === "/company-profile") {
      return "Profile setting";
    }
    if (pathname === "/super-admin-dashboard") {
      return "Super admin dashboard";
    }
    if (pathname === "/create-member") {
      const isEditMode = searchParams.get("edit") !== null;
      return isEditMode ? "Edit a user profile" : "Create a user profile";
    }
    // Default title if route doesn't match
    return title || "Jobs";
  };

  // Get subtitle based on current route
  const getSubtitleFromRoute = (pathname: string): string => {
    if (pathname === "/profile-setting") {
      // Check URL search params for active tab
      const tab = searchParams.get("tab");
      if (tab === "company") {
        return "Company profile";
      }
      return "Personal profile";
    }
    if (pathname === "/all-jobs") {
      return "All jobs";
    }
    if (pathname === "/create-job") {
      return "Creation form";
    }
    if (pathname.startsWith("/jobs/")) {
      return "Overview";
    }
    if (pathname === "/job-matches") {
      return "In Match";
    }
    if (pathname.startsWith("/candidate/")) {
      return "In match";
    }
    // Default subtitle if route doesn't match
    return subtitle || "";
  };

  const currentTitle = title || getTitleFromRoute(location.pathname);
  const currentSubtitle = subtitle || getSubtitleFromRoute(location.pathname);

  // Close dropdown if clicked outside
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch user profile picture if not already in Redux state
  useEffect(() => {
    if (!accessToken || profilePictureId !== null) return;

    let isMounted = true;
    const loadProfilePicture = async () => {
      try {
        // Fetch current user to get company_member_id
        const ensuredUser = await dispatch(ensureCurrentUser()).unwrap();
        const companyMemberId = ensuredUser.company_member_id as number | undefined;

        if (!companyMemberId || !isMounted) return;

        // Fetch company member to get profile_picture
        const companyMemberResponse = await fetchCompanyMember(companyMemberId, accessToken);
        if (!isMounted) return;

        const data = companyMemberResponse.data;
        if (data?.profile_picture) {
          // Handle both number and object formats
          let pictureId: number | null = null;
          if (typeof data.profile_picture === "number") {
            pictureId = data.profile_picture;
          } else if (
            typeof data.profile_picture === "object" &&
            "id" in data.profile_picture
          ) {
            pictureId = (data.profile_picture as { id: number }).id;
          }
          
          if (pictureId !== null && isMounted) {
            dispatch(updateProfilePicture(pictureId));
          }
        }
      } catch (error) {
        console.error("Failed to fetch profile picture:", error);
      }
    };

    loadProfilePicture();

    return () => {
      isMounted = false;
    };
  }, [accessToken, user?.company_member_id, profilePictureId, dispatch]);

  // Fetch company profile picture
  useEffect(() => {
    if (!accessToken || companyProfilePictureId !== null) return;

    let isMounted = true;
    const loadCompanyPicture = async () => {
      try {
        // Fetch current user to get company_member_id
        const ensuredUser = await dispatch(ensureCurrentUser()).unwrap();
        const companyMemberId = ensuredUser.company_member_id as number | undefined;

        if (!companyMemberId || !isMounted) return;

        // Fetch company member to get company_id
        const companyMemberResponse = await fetchCompanyMember(companyMemberId, accessToken);
        if (!isMounted) return;

        const companyId = companyMemberResponse.data?.company?.id;
        if (!companyId) return;

        // Fetch company data to get profile_picture
        const companyResponse = await fetchCompany(companyId, accessToken);
        if (!isMounted) return;

        const companyData = companyResponse.data;
        const directCompanyPictureUrl =
          typeof (companyData as { profile_picture_url?: unknown } | undefined)?.profile_picture_url === "string"
            ? ((companyData as { profile_picture_url?: string }).profile_picture_url ?? null)
            : null;

        if (companyData?.profile_picture) {
          let pictureId: number | null = null;
          if (typeof companyData.profile_picture === "number") {
            pictureId = companyData.profile_picture;
          } else if (
            typeof companyData.profile_picture === "object" &&
            "id" in companyData.profile_picture
          ) {
            pictureId = (companyData.profile_picture as { id: number }).id;
          }

          if (pictureId !== null && isMounted) {
            dispatch(updateCompanyProfilePicture(pictureId));
            if (directCompanyPictureUrl) {
              // Prefer direct API URL and skip file-id URL fetch.
              dispatch(updateCompanyProfilePictureUrl(directCompanyPictureUrl));
            } else {
              dispatch(updateCompanyProfilePictureUrl(null));
              getProfilePictureUrl(
                pictureId,
                accessToken || undefined,
                "/assets/images/Fallback-Company-No-Image.jpg"
              )
                .then((url) => {
                  if (isMounted && url) {
                    dispatch(updateCompanyProfilePictureUrl(url));
                  }
                })
                .catch(() => {});
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch company picture:", error);
      }
    };

    loadCompanyPicture();

    return () => {
      isMounted = false;
    };
  }, [accessToken, user?.company_member_id, companyProfilePictureId, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside both the avatar button AND the dropdown menu
      if (
        avatarRef.current &&
        dropdownRef.current &&
        !avatarRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent double clicks

    setIsLoggingOut(true);
    setDropdownOpen(false);

    clearTokens();

    dispatch(logout());

    try {
      sessionStorage.clear();
      localStorage.clear();
    } catch (e) {
      console.warn("Storage clear failed:", e);
    }

    setTimeout(() => {
      window.location.href = "/login";
    }, 100);
  };

  return (
    <header className="top-0 z-50 bg-[#E6EDDD] px-6 py-4">
      <div className="flex items-center justify-between gap-4">
        {/* Left Section: Title and Subtitle with underline */}
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex flex-col relative">
            <h1 className="text-2xl font-semibold text-gray-900 leading-none pb-2">{currentTitle}</h1>
            <div className="h-[1.5px] bg-black/80 w-full mb-2" />
            <p className="text-sm text-gray-600 font-medium">{currentSubtitle}</p>
          </div>
        </div>

        {/* Right Section: Create Job, Avatar */}
        <div className="flex items-center gap-3 shrink-0">
          {/* {isCompanyRecruiter && (
            <button
              type="button"
              onClick={() => navigate("/create-job")}
              aria-label="Create a job"
              className="group btn-cta flex h-10 items-center overflow-hidden rounded-md transition-[max-width,box-shadow] duration-300 ease-out max-w-10 hover:max-w-[168px] focus-visible:max-w-[168px] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/20"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center">
                <Plus size={20} strokeWidth={2.5} />
              </span>
              <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-semibold opacity-0 transition-all duration-300 ease-out group-hover:max-w-[120px] group-hover:pr-4 group-hover:opacity-100 group-focus-visible:max-w-[120px] group-focus-visible:pr-4 group-focus-visible:opacity-100">
                Create a job
              </span>
            </button>
          )} */}

          {/* Notification Icon */}
          {/* <button className="p-2 hover:bg-black/5 rounded-md transition-colors relative">
            <Bell size={24} className="text-gray-400" />
          </button> */}

          {/* User Avatar */}
          <div className="relative">
            <button
              ref={avatarRef}
              className="w-10 h-10 rounded-md overflow-hidden hover:opacity-90 transition-opacity border border-black shadow-sm"
              onClick={() => setDropdownOpen((open) => !open)}
              disabled={isLoggingOut}
            >
              <ProfileImage
                profilePicture={profilePictureId}
                profilePictureUrl={
                  typeof user?.profile_picture_url === "string"
                    ? user.profile_picture_url
                    : null
                }
                accessToken={accessToken || undefined}
                alt="User"
                className="w-full h-full object-cover grayscale-[0.2]"
                fallbackPath="/assets/images/Avatar2.jpg"
              />
            </button>
            {dropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute z-50 right-0 mt-2 w-40 bg-white border border-gray-100 rounded-md shadow-lg text-sm py-1"
              >
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-red-600 hover:bg-gray-50 transition-colors"
                >
                  <LogOut size={16} />
                  {isLoggingOut ? "Logging out..." : "Logout"}
                </button>
              </div>
            )}
          </div>

          {/* <button className="w-10 h-10 flex items-center justify-center bg-white rounded-md shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors overflow-hidden">
              <ProfileImage
                profilePicture={companyProfilePictureId}
                profilePictureUrl={companyProfilePictureUrl}
                accessToken={accessToken || undefined}
                alt="Company"
                className="w-full h-full object-cover"
                fallbackPath="/assets/images/Fallback-Company-No-Image.jpg"
              />
          </button> */}
        </div>
      </div>
    </header>
  )
}

export default Header
